import { json, redirect } from "@remix-run/node";
import type { ActionFunctionArgs, LoaderFunctionArgs } from "@remix-run/node";
import { Form, useActionData, useNavigation, NavLink } from "@remix-run/react";
import { Button, Text, TextField, RadixForm, Flex } from "@figment-fitness/ui";
import {
  getAuthSession,
  signInWithEmail,
} from "@figment-fitness/database/auth";
import { TitleBodyContainer } from "@figment-fitness/ui/components/title-body-container";
import { fitnessRoutes } from "~/utils/fitness/routes";
import DefaultErrorBoundary from "@figment-fitness/ui/components/error-boundary";
import { auth } from "~/lib/auth.server";

export const loader = async ({ request }: LoaderFunctionArgs) => {
  const session = await getAuthSession(auth, request);

  if (session?.user) {
    throw redirect(fitnessRoutes.top());
  }

  return json({ success: false });
};

export const action = async ({ request }: ActionFunctionArgs) => {
  const formData = await request.formData();
  const result = await signInWithEmail(auth, request, {
    email: formData.get("email") as string,
    password: formData.get("password") as string,
  });

  if (!result.ok) {
    return json({ success: false, error: "ログインに失敗しました" });
  }

  throw redirect(fitnessRoutes.top(), { headers: result.headers });
};

export const ErrorBoundary = DefaultErrorBoundary;

export default function Login() {
  const actionData = useActionData<typeof action>();
  const navigation = useNavigation();

  return (
    <TitleBodyContainer
      title="フィットネスクラブ向け管理画面ログイン"
      level={1}
    >
      <Flex direction="column" gap="4">
        <RadixForm.Root
          method="post"
          className="space-y-4 w-full max-w-md mx-auto mt-8"
        >
          <Form method="post">
            <Flex direction="column" gap="4">
              <RadixForm.Field name="email">
                <RadixForm.Label>メールアドレス</RadixForm.Label>
                <RadixForm.Control asChild>
                  <TextField.Root type="email" radius="large" required />
                </RadixForm.Control>
                <RadixForm.Message match="valueMissing">
                  メールアドレスを入力してください
                </RadixForm.Message>
                <RadixForm.Message match="typeMismatch">
                  有効なメールアドレスを入力してください
                </RadixForm.Message>
              </RadixForm.Field>

              <RadixForm.Field name="password">
                <RadixForm.Label>パスワード</RadixForm.Label>
                <RadixForm.Control asChild>
                  <TextField.Root type="password" radius="large" required />
                </RadixForm.Control>
                <RadixForm.Message match="valueMissing">
                  パスワードを入力してください
                </RadixForm.Message>
              </RadixForm.Field>

              {actionData?.error && (
                <Text color="red" size="2">
                  {actionData.error}
                </Text>
              )}

              <RadixForm.Submit asChild>
                <Button disabled={navigation.state === "submitting"}>
                  {navigation.state === "submitting"
                    ? "ログイン中..."
                    : "ログイン"}
                </Button>
              </RadixForm.Submit>
            </Flex>
          </Form>
        </RadixForm.Root>

        <Text color="blue">
          <NavLink to={fitnessRoutes.authSendPasswordResetEmail()}>
            パスワードをお忘れですか?
          </NavLink>
        </Text>
      </Flex>
    </TitleBodyContainer>
  );
}
